import React, { useState, useEffect } from 'react';
import { Modal, Accordion, Spinner } from 'react-bootstrap';
import {
  RolePermissionsModalProps,
  PermissionResponse,
  NestedPermissions
} from '../types/types';
import { GET_ROLE_DETAIL } from 'services/api/role';
import toast from 'react-hot-toast';

const RoleDetailViewModal: React.FC<RolePermissionsModalProps> = ({
  show,
  onHide,
  role
}) => {
  const [permissions, setPermissions] = useState<PermissionResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchRolePermissions = async () => {
      if (!show || !role) return;

      setLoading(true);
      try {
        const response = await GET_ROLE_DETAIL((role as any)?.id || role?._id);
        setPermissions(response?.data?.data?.permissions || []);
      } catch (err) {
        console.error('Error fetching role permissions:', err);
        toast.error('Failed to fetch role permissions');
      } finally {
        setLoading(false);
      }
    };

    fetchRolePermissions();
  }, [show, role]);

  const groupedPermissions = permissions.reduce<NestedPermissions>(
    (acc, permission) => {
      const { category, subcategory, nestedSubcategory } = permission;

      if (!acc[category]) {
        acc[category] = {};
      }

      if (!acc[category][subcategory]) {
        acc[category][subcategory] = {
          regular: [],
          nested: {}
        };
      }

      if (nestedSubcategory) {
        if (!acc[category][subcategory].nested[nestedSubcategory]) {
          acc[category][subcategory].nested[nestedSubcategory] = [];
        }
        acc[category][subcategory].nested[nestedSubcategory].push(permission);
      } else {
        acc[category][subcategory].regular.push(permission);
      }

      return acc;
    },
    {}
  );

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Role Permissions: {role?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : permissions?.length === 0 ? (
          <div className="text-center">No permissions assigned</div>
        ) : (
          <Accordion>
            {Object.entries(groupedPermissions).map(
              ([category, subcategories], catIndex) => (
                <Accordion.Item key={category} eventKey={String(catIndex)}>
                  <Accordion.Header>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </Accordion.Header>
                  <Accordion.Body>
                    <Accordion>
                      {Object.entries(subcategories).map(
                        ([subcategory, subPermData], subIndex) => {
                          const { regular, nested } = subPermData;
                          const hasNestedSubcategories =
                            Object.keys(nested).length > 0;

                          return (
                            <Accordion.Item
                              key={subcategory}
                              eventKey={String(subIndex)}
                            >
                              <Accordion.Header>
                                {subcategory.charAt(0).toUpperCase() +
                                  subcategory.slice(1)}
                              </Accordion.Header>
                              <Accordion.Body>
                                {regular.length > 0 && (
                                  <>
                                    <h6>General</h6>
                                    <ul className="list-group mb-3">
                                      {regular.map(permission => (
                                        <li
                                          key={
                                            (permission as any).id ||
                                            permission._id
                                          }
                                          className="list-group-item d-flex justify-content-between align-items-center"
                                        >
                                          {permission.name}
                                          <span className="badge bg-primary rounded-pill">
                                            {permission.action}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  </>
                                )}

                                {hasNestedSubcategories &&
                                  Object.entries(nested).map(
                                    ([nestedKey, nestedPerms]) => {
                                      const nestedDisplayName =
                                        nestedPerms[0]
                                          ?.nestedSubcategoryDisplayName ||
                                        nestedKey;
                                      return (
                                        <div
                                          key={nestedKey}
                                          className="ms-3 mb-3"
                                        >
                                          <h6>{nestedDisplayName}</h6>
                                          <ul className="list-group">
                                            {nestedPerms.map(permission => (
                                              <li
                                                key={
                                                  (permission as any).id ||
                                                  permission._id
                                                }
                                                className="list-group-item d-flex justify-content-between align-items-center"
                                              >
                                                {permission.name}
                                                <span className="badge bg-primary rounded-pill">
                                                  {permission.action}
                                                </span>
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      );
                                    }
                                  )}
                              </Accordion.Body>
                            </Accordion.Item>
                          );
                        }
                      )}
                    </Accordion>
                  </Accordion.Body>
                </Accordion.Item>
              )
            )}
          </Accordion>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default RoleDetailViewModal;
